// =============================================================================
// BillsToPayScreen — /ap/bills  (T-362, T-375, T-377)
// The "what's owing" view: supplier bills entered in Xero but not yet paid
// (status='entered_in_xero' — card spends go straight to paid so they never
// land here). Sorted by due date, overdue in red / due-soon amber.
//
// T-375: rows open a full BILL DETAIL modal — supplier, amounts, dates, coding,
//        the invoice file (view + download) — so you review BEFORE paying.
//        Mark-paid lives inside the detail view (quick button kept on the row).
// T-377: from the detail view you can UPLOAD or REPLACE the invoice file
//        (revised invoice / card-surcharge invoice / bill lodged without one —
//        attach on pay). Replacing never deletes the old file from storage and
//        stamps an audit note. Amount can be adjusted (e.g. +card surcharge)
//        with a reason, also audit-noted.
// 23 Sep 2026: a bundled reimbursement's total is XERO's (xero_bill_total,
//        mig 387), not the sum of its rows; when the two disagree, or the rows
//        are owed to different people, the group gets a red chip and Mark paid
//        asks first. Born of the Fernandez brothers' bills landing on one draft.
// =============================================================================

import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Banknote, AlertTriangle, CalendarClock, CheckCircle2,
  FileText, Upload, Eye, Download, Pencil, Copy, Check,
  ChevronRight, ChevronDown,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { splitFromInc } from "@/lib/gst";   // T-403
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Button, Field, Input, Spinner, EmptyState } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

interface BillRow {
  ap_invoice_id: string;
  invoice_number: string | null;
  supplier_name: string | null;
  amount_inc_gst: number | null;
  amount_ex_gst: number | null;
  due_date: string | null;
  invoice_date: string | null;
  budget_code: string | null;
  project_id: string | null;
  xero_bill_number: string | null;
  xero_bill_id: string | null;
  // The gate for everything on this screen: only entered_in_xero reaches here,
  // and both mark-paid paths check it again before writing.
  status: string;
  pdf_storage_path: string | null;
  pdf_original_filename: string | null;
  description: string | null;
  payment_method: string | null;
  notes: string | null;
  is_reimbursement: boolean | null;
  reimburse_employee_id: string | null;
  captured_by: string | null;
  captured_at: string | null;
  capture_source: string | null;
  // Mig 387 (23 Sep 2026) - what Xero reported for the bill this row sits on,
  // read back by the bundle push. Null on anything pushed before then.
  xero_bill_total?: number | null;
  xero_total_mismatch?: boolean | null;
  _proj?: string | null;
  _reimbName?: string | null;   // "pay this back to X" — surfaced so a staff
                                // reimbursement never looks like a vendor bill
}

function daysTo(d: string | null): number | null {
  if (!d) return null;
  return Math.round((new Date(d + "T23:59:59").getTime() - Date.now()) / 86400000);
}
function dueTone(d: string | null): { cls: string; label: string } {
  const n = daysTo(d);
  if (n == null) return { cls: "text-text-muted", label: "no due date" };
  if (n < 0) return { cls: "text-danger", label: `${Math.abs(n)}d overdue` };
  if (n === 0) return { cls: "text-danger", label: "due today" };
  if (n <= 7) return { cls: "text-warn", label: `due in ${n}d` };
  return { cls: "text-text-muted", label: `due ${fmtDate(d)}` };
}

export default function BillsToPayScreen() {
  const user = useRequireAuth();
  const canPay = hasRole(user, "manager");
  const [detail, setDetail] = useState<BillRow | null>(null);   // T-375 — review first
  const [detailInBundle, setDetailInBundle] = useState(false);  // G4 — viewing one line of a bundle
  const [openBundle, setOpenBundle] = useState<string | null>(null);
  const [payingBundle, setPayingBundle] = useState<string | null>(null);
  const [bundleError, setBundleError] = useState<string | null>(null);

  // One payment, one action. Marking every row on the bill in a single update
  // means a partially-paid bundle is not a state that can exist.
  async function markBundlePaid(billId: string, rows: BillRow[]) {
    // Same guard as the single-bill path. A bundle reaches this screen only
    // after the Worker has pushed it, so this is insurance, not logic.
    const notInXero = rows.filter(r => r.status !== "entered_in_xero");
    if (notInXero.length) {
      setBundleError(
        `${notInXero.length} line${notInXero.length === 1 ? "" : "s"} on this bill `
        + "aren't in Xero yet, so it can't be marked paid. Push the bundle from the "
        + "Reimbursements screen first.");
      return;
    }
    setPayingBundle(billId); setBundleError(null);
    const { error } = await supabase.from("ap_invoices").update({
      status:    "paid",
      paid_date: new Date().toISOString().slice(0, 10),
    }).in("ap_invoice_id", rows.map(r => r.ap_invoice_id));
    setPayingBundle(null);
    if (error) { setBundleError(error.message); return; }
    setOpenBundle(null);
    refetch();
  }

  const { data: bills = [], isLoading, refetch } = useQuery({
    queryKey: ["bills_to_pay"],
    enabled: !!user && canPay,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ap_invoices")
        .select("ap_invoice_id, invoice_number, supplier_name, amount_inc_gst, amount_ex_gst, gst_amount, due_date, invoice_date, budget_code, project_id, xero_bill_number, xero_bill_id, pdf_storage_path, pdf_original_filename, description, payment_method, notes, is_reimbursement, reimburse_employee_id, captured_by, captured_at, capture_source, status, xero_entry_type, gst_mismatch, gst_xero_expected, xero_bill_total, xero_total_mismatch")
        // -------------------------------------------------------------------
        // BILLS TO PAY IS A PAYMENT QUEUE, NOT A BACKLOG. 21 Aug 2026.
        //
        // James, describing the process this screen sits in:
        //
        //   "A Bill To Pay should only ever be in that queue after it's gone to
        //    Xero. Receipt lands, worker creates it in Ops, we code it and
        //    approve it. If Spend Money -> To Xero queue. If Bill -> To Xero. If
        //    Reimbursement -> Reimbursement queue, and once bundled it pushes the
        //    bill into Xero. So Bills to Pay should all be in Xero, as either a
        //    bill to a supplier or a bill with lines of reimbursement to the
        //    employee. In Bills to Pay we track and pay in NAB banking and record
        //    the payment in Ops. When that payment hits Xero there's a bill
        //    waiting to reconcile against."
        //
        // T-483 previously let status = 'approved' through, so that an approved
        // bill would not "go late" waiting to be hand-keyed. Right problem,
        // wrong screen: it put bills here that Xero had never heard of, and on
        // 20 Aug one was marked paid with no Xero bill behind it, leaving a bank
        // line with nothing to reconcile against.
        //
        // The backlog protection T-483 wanted now lives where it belongs - the
        // Xero hand-off screen warns when approved bills are sitting unpushed.
        //
        // status is the honest gate for all three routes into Xero: the Worker
        // push, the reimbursement bundle push, and hand-keying on the hand-off
        // screen all set entered_in_xero. xero_bill_id does NOT work here - a
        // hand-keyed bill never gets one.
        // -------------------------------------------------------------------
        // A reimbursement that has been BUNDLED (it carries a xero_bill_id) is
        // a bill to the person whatever entry type was picked at approval - a
        // stale spend-money override on one receipt was hiding it from this
        // list, splitting its bundle and losing the Uber line (James, 26 Aug).
        // The bundle push now normalises the flag too; this keeps old rows right.
        .eq("status", "entered_in_xero")
        .or("xero_entry_type.eq.bill,is_reimbursement.eq.true")
        // The G4 reimbursement guard that used to sit here is gone, and
        // deliberately so. It existed because 'approved' rows reached this
        // screen; now that only entered_in_xero does, a reimbursement cannot
        // arrive until the bundle has been pushed - the bundle route sets
        // status and xero_bill_id in the same write. Keeping the guard would
        // also have wrongly hidden a reimbursement somebody keyed into Xero by
        // hand, which has no xero_bill_id but is genuinely in Xero.
        .order("due_date", { ascending: true, nullsFirst: false })
        .limit(300);
      if (error) throw error;
      // unknown-first: database.types.ts predates gst_mismatch/gst_xero_expected (mig 355)
      // and xero_bill_total/xero_total_mismatch (mig 387)
      const rows = (data ?? []) as unknown as BillRow[];
      const pids = Array.from(new Set(rows.map(r => r.project_id).filter(Boolean)));
      if (pids.length) {
        const { data: ps } = await supabase.from("projects").select("project_id, project_code").in("project_id", pids as string[]);
        const by = new Map((ps ?? []).map((p: any) => [p.project_id, p.project_code]));
        rows.forEach(r => { r._proj = r.project_id ? by.get(r.project_id) ?? null : null; });
      }
      // Reimbursements — resolve who gets paid back so they never read as vendor bills.
      const eids = Array.from(new Set(rows.map(r => r.reimburse_employee_id).filter(Boolean)));
      if (eids.length) {
        const { data: es } = await supabase.from("employees").select("employee_id, first_name, last_name").in("employee_id", eids as string[]);
        const byE = new Map((es ?? []).map((e: any) => [e.employee_id, [e.first_name, e.last_name].filter(Boolean).join(" ")]));
        rows.forEach(r => { r._reimbName = r.reimburse_employee_id ? byE.get(r.reimburse_employee_id) ?? null : null; });
      }
      return rows;
    },
  });

  // ---------------------------------------------------------------------------
  // G4 — a bundled reimbursement is ONE bill in Xero spread across several ops
  // rows. Listing them separately would show five bills owing where one is owed,
  // and would have you mark the same payment paid five times.
  //
  // Grouped on xero_bill_id, and only when it actually holds more than one row -
  // an ordinary supplier bill is left exactly as it was.
  // ---------------------------------------------------------------------------
  const groups = useMemo(() => {
    const byBill = new Map<string, BillRow[]>();
    const singles: BillRow[] = [];
    for (const b of bills) {
      if (!b.xero_bill_id) { singles.push(b); continue; }
      const list = byBill.get(b.xero_bill_id) ?? [];
      list.push(b);
      byBill.set(b.xero_bill_id, list);
    }
    type Group =
      | { kind: "single"; key: string; bill: BillRow }
      | { kind: "bundle"; key: string; rows: BillRow[]; total: number;
          due_date: string | null; billNumber: string | null; who: string;
          // 23 Sep 2026 - the total shown is Xero's when the push read one
          // back; the rows' sum is kept beside it so a disagreement is visible.
          rowsTotal: number; xeroTotal: number | null; mismatch: boolean; whoMixed: boolean };

    const out: Group[] = [];
    for (const [billId, rows] of byBill) {
      if (rows.length === 1) { singles.push(rows[0]!); continue; }
      const rowsTotal = rows.reduce((a, r) => a + Number(r.amount_inc_gst ?? 0), 0);
      const xeroTotal = rows.find(r => r.xero_bill_total != null)?.xero_bill_total ?? null;
      // James, 23 Sep 2026: two brothers' bundles landed on ONE Xero bill and this
      // screen summed them to $165.80 against Xero's $40. The bill's total is
      // Xero's to state, not ours to add up. A group whose rows are owed to
      // more than one person, or whose sum differs from what Xero holds, or
      // that the push itself flagged, gets the red chip and keeps Mark paid
      // out of reach until someone has looked at the bill in Xero.
      const people   = new Set(rows.map(r => r.reimburse_employee_id ?? r.supplier_name ?? ""));
      const whoMixed = people.size > 1;
      // A cent per receipt of extractor rounding (ex + GST a cent off the
      // docket total) is not a disagreement; the Fernandez case was $125.80.
      const mismatch = whoMixed
        || rows.some(r => r.xero_total_mismatch === true)
        || (xeroTotal != null && Math.abs(Math.round(xeroTotal * 100) - Math.round(rowsTotal * 100)) > rows.length);
      out.push({
        kind: "bundle",
        key: billId,
        rows,
        total: xeroTotal ?? rowsTotal,
        rowsTotal,
        xeroTotal,
        mismatch,
        whoMixed,
        due_date: rows[0]?.due_date ?? null,
        billNumber: rows[0]?.xero_bill_number ?? null,
        // Every row on a reimbursement bundle is owed to the same person.
        who: whoMixed
          ? Array.from(new Set(rows.map(r => r._reimbName ?? r.supplier_name ?? "?"))).join(" + ")
          : (rows[0]?._reimbName ?? rows[0]?.supplier_name ?? "one bill"),
      });
    }
    for (const b of singles) out.push({ kind: "single", key: b.ap_invoice_id, bill: b });

    // Oldest due first, same as the flat list was.
    return out.sort((a, b) => {
      const da = a.kind === "bundle" ? a.due_date : a.bill.due_date;
      const db = b.kind === "bundle" ? b.due_date : b.bill.due_date;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db);
    });
  }, [bills]);

  const kpis = useMemo(() => {
    const total = bills.reduce((a, b) => a + Number(b.amount_inc_gst ?? 0), 0);
    const overdue = bills.filter(b => { const n = daysTo(b.due_date); return n != null && n < 0; });
    const overdueVal = overdue.reduce((a, b) => a + Number(b.amount_inc_gst ?? 0), 0);
    const soon = bills.filter(b => { const n = daysTo(b.due_date); return n != null && n >= 0 && n <= 7; }).length;
    // Count bills, not ops rows — a five-receipt bundle is one bill owing.
    return { count: groups.length, total, overdue: overdue.length, overdueVal, soon };
  }, [bills, groups]);

  if (!user) return null;
  if (!canPay) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><EmptyState icon={AlertTriangle} title="Managers only" description="Bills to pay is available to managers and directors." /></Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto pb-20">
      <Link to="/ap" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"><ArrowLeft size={14} /> AP / Spend</Link>

      <div className="mb-5">
        <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Banknote size={20} className="text-accent" /> Bills to pay
        </h1>
        <p className="text-text-muted text-[13px] mt-1">
          Supplier bills entered in Xero and awaiting payment — oldest due first. Click a bill to review the details + invoice file, then mark it paid.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label="Bills owing" value={kpis.count} icon={Banknote} />
        <KPI label="Total owing" value={fmtMoney(kpis.total)} />
        <KPI label="Overdue" value={kpis.overdue} icon={AlertTriangle} accent={kpis.overdue > 0} sub={fmtMoney(kpis.overdueVal)} />
        <KPI label="Due ≤ 7 days" value={kpis.soon} icon={CalendarClock} accent={kpis.soon > 0} />
      </div>

      {isLoading ? (
        <div className="p-12 flex justify-center"><Spinner size={20} /></div>
      ) : bills.length === 0 ? (
        <Card><EmptyState icon={CheckCircle2} title="No bills owing" description="Every bill in Xero is paid. Anything coded and approved but not yet sent across is waiting on the Xero hand-off screen." /></Card>
      ) : (
        <div className="space-y-2">
          {bundleError && (
            <Card><div className="p-3 text-[12px] text-danger">Couldn't mark the bill paid: {bundleError}</div></Card>
          )}
          {groups.map(gp => {
            // ---- a bundled reimbursement: one bill, several receipts ----------
            if (gp.kind === "bundle") {
              const t = dueTone(gp.due_date);
              const open = openBundle === gp.key;
              const busy = payingBundle === gp.key;
              return (
                <Card key={gp.key}>
                  <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
                    <button type="button"
                            onClick={() => setOpenBundle(open ? null : gp.key)}
                            className="min-w-0 flex-1 text-left flex items-center gap-2">
                      {open
                        ? <ChevronDown  size={15} className="text-text-muted flex-shrink-0" />
                        : <ChevronRight size={15} className="text-text-muted flex-shrink-0" />}
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="text-[14px] font-medium text-text">{gp.who}</span>
                          <span className={`text-[11px] font-mono ${t.cls}`}>{t.label}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent/15 text-accent border border-accent/30 font-medium">
                            Reimbursement · {gp.rows.length} receipts
                          </span>
                          {/* 23 Sep 2026 - Xero and Ops disagree about this bill.
                              Do not pay it off this screen until it is sorted in Xero. */}
                          {gp.mismatch && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded bg-danger/15 text-danger border border-danger/30 font-medium"
                              title={
                                gp.whoMixed
                                  ? "Receipts owed to different people share one Xero bill — the bill can only be paying one of them."
                                  : `Xero holds ${gp.xeroTotal != null ? fmtMoney(gp.xeroTotal) : "an unknown total"}; these receipts add to ${fmtMoney(gp.rowsTotal)}.`
                              }
                            >
                              {gp.whoMixed ? "Two people on one bill — fix in Xero" : "Total differs from Xero — fix in Xero"}
                            </span>
                          )}
                        </span>
                        <span className="block text-[11px] text-text-muted mt-0.5">
                          One bill in Xero{gp.billNumber ? <> · <span className="font-mono">{gp.billNumber}</span></> : null}
                          {gp.xeroTotal != null
                            ? <> · Xero total <span className="font-mono">{fmtMoney(gp.xeroTotal)}</span></>
                            : <> · total not read back from Xero (pushed before 23 Sep)</>}
                        </span>
                      </span>
                    </button>
                    <div className="text-right flex-shrink-0">
                      <div className={`font-mono text-[15px] ${gp.mismatch ? "text-danger" : "text-text"}`}>{fmtMoney(gp.total)}</div>
                      {gp.mismatch && gp.xeroTotal != null && (
                        <div className="text-[10px] text-text-muted font-mono">receipts {fmtMoney(gp.rowsTotal)}</div>
                      )}
                    </div>
                    <Button size="sm" icon={CheckCircle2} disabled={busy}
                            variant={gp.mismatch ? "danger" : "primary"}
                            title={gp.mismatch ? "Ops and Xero disagree about this bill — check it in Xero before paying." : undefined}
                            onClick={() => {
                              // Not a hard block: there is no undo for a flag, so a wrong
                              // one must never strand a bill. Make the person say so.
                              if (gp.mismatch && !window.confirm(
                                `Ops and Xero disagree about this bill${gp.xeroTotal != null ? ` (Xero holds ${fmtMoney(gp.xeroTotal)}, receipts add to ${fmtMoney(gp.rowsTotal)})` : ""}. `
                                + "Have you checked it in Xero and paid what Xero shows?")) return;
                              markBundlePaid(gp.key, gp.rows);
                            }}>
                      {busy ? "Saving…" : "Mark paid"}
                    </Button>
                  </div>

                  {open && (
                    <div className="border-t border-line divide-y divide-line">
                      {gp.rows.map(r => (
                        <div key={r.ap_invoice_id} className="px-4 py-2.5 flex items-center gap-3">
                          <FileText size={13} className="text-text-muted flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] text-text truncate">{r.supplier_name ?? r.description ?? "Receipt"}</div>
                            <div className="text-[11px] text-text-muted mt-0.5 flex gap-3 flex-wrap">
                              {r.invoice_date && <span>{fmtDate(r.invoice_date)}</span>}
                              {r.budget_code && <span className="font-mono">{r.budget_code}</span>}
                              {r._proj && <span className="font-mono text-accent">{r._proj}</span>}
                            </div>
                          </div>
                          <div className="font-mono text-[13px] text-text flex-shrink-0">{fmtMoney(r.amount_inc_gst ?? 0)}</div>
                          <Button size="sm" variant="secondary" icon={Eye}
                                  onClick={() => { setDetailInBundle(true); setDetail(r); }}>
                            View
                          </Button>
                        </div>
                      ))}
                      <div className="px-4 py-2 text-[11px] text-text-dim">
                        These are lines on one Xero bill. Marking it paid settles all of them together.
                      </div>
                    </div>
                  )}
                </Card>
              );
            }

            // ---- an ordinary bill: unchanged ---------------------------------
            const b = gp.bill;
            const t = dueTone(b.due_date);
            return (
              <Card key={b.ap_invoice_id}>
                <div
                  className="p-4 flex items-center justify-between gap-3 flex-wrap cursor-pointer hover:bg-ink-raised/50 transition-colors rounded-lg"
                  onClick={() => setDetail(b)}
                  title="Review this bill before paying"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[14px] font-medium text-text">{b.supplier_name ?? "—"}</span>
                      <span className={`text-[11px] font-mono ${t.cls}`}>{t.label}</span>
                      {b.is_reimbursement && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent/15 text-accent border border-accent/30 font-medium">
                          Reimburse {b._reimbName ?? "employee"}
                        </span>
                      )}
                      {!b.pdf_storage_path && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-warn/15 text-warn border border-warn/30">no invoice attached</span>
                      )}
                      {/* T-AP-GST-GUARD (mig 355) — stamped by the push when the
                          document's GST and Xero's derived GST disagree. */}
                      {(b as any).gst_mismatch && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-danger/15 text-danger border border-danger/30 font-medium"
                          title={`Document GST $${Number((b as any).gst_amount ?? 0).toFixed(2)} · Xero will derive $${Number((b as any).gst_xero_expected ?? 0).toFixed(2)} — fix the lines/tax codes on the Xero bill (note is on its history)`}
                        >
                          GST mismatch — fix in Xero before Approve
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-text-muted mt-0.5 flex flex-wrap items-center gap-x-3">
                      {b.invoice_number && <span className="font-mono">{b.invoice_number}</span>}
                      {b.budget_code && <span className="font-mono">{b.budget_code}</span>}
                      {b._proj && <span className="font-mono text-accent">{b._proj}</span>}
                      {b.xero_bill_number && <span>Xero {b.xero_bill_number}</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-mono text-[15px] text-text">{fmtMoney(b.amount_inc_gst ?? 0)}</div>
                  </div>
                  <Button size="sm" variant="secondary" icon={Eye} onClick={(e: any) => { e.stopPropagation(); setDetailInBundle(false); setDetail(b); }}>Review</Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {detail && (
        <BillDetailModal
          bill={detail}
          partOfBundle={detailInBundle}
          onClose={() => { setDetail(null); setDetailInBundle(false); }}
          onChanged={() => { setDetail(null); setDetailInBundle(false); refetch(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// BillDetailModal (T-375 + T-377) — review everything, manage the invoice file,
// adjust the amount (surcharge), then mark paid — all in one place.
// -----------------------------------------------------------------------------
function BillDetailModal({ bill, partOfBundle = false, onClose, onChanged }: { bill: BillRow; partOfBundle?: boolean; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);   // 'upload' | 'save' | 'pay' | null

  // Local copies so edits show immediately.
  const [pdfPath, setPdfPath] = useState(bill.pdf_storage_path);
  const [pdfName, setPdfName] = useState(bill.pdf_original_filename);
  const [amountInc, setAmountInc] = useState<number>(Number(bill.amount_inc_gst ?? 0));

  // Amount adjust (T-377 — card surcharge etc).
  const [editAmount, setEditAmount] = useState(false);
  const [newInc, setNewInc] = useState(String(bill.amount_inc_gst ?? ""));
  const [adjustReason, setAdjustReason] = useState("card surcharge");

  // Pay
  const [paidDate, setPaidDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState("");

  // Who loaded it — resolve captured_by (users.user_id) to a person for the
  // bank-rec view, so provenance survives past the coding step.
  const { data: capturedByName } = useQuery({
    queryKey: ["bill_captured_by", bill.captured_by],
    enabled: !!bill.captured_by,
    queryFn: async () => {
      const { data } = await supabase
        .from("users")
        .select("login_email, employees:employee_id ( first_name, last_name )")
        .eq("user_id", bill.captured_by!)
        .maybeSingle();
      const u: any = data;
      if (!u) return null;
      return [u.employees?.first_name, u.employees?.last_name].filter(Boolean).join(" ") || u.login_email || null;
    },
  });

  // Invoice file — signed URL, with pending_receipts fallback (same as ApXeroScreen).
  const { data: receipt } = useQuery({
    queryKey: ["bill_detail_receipt", bill.ap_invoice_id, pdfPath],
    queryFn: async () => {
      let path: string | null = pdfPath ?? null;
      if (!path) {
        const { data: pr } = await supabase
          .from("pending_receipts")
          .select("storage_path")
          .eq("promoted_to_ap_invoice_id", bill.ap_invoice_id)
          .order("promoted_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        path = (pr as any)?.storage_path ?? null;
      }
      if (!path) return null;
      const { data } = await supabase.storage.from("receipt-captures").createSignedUrl(path, 3600);
      return { url: data?.signedUrl ?? null, path };
    },
  });

  async function downloadReceipt() {
    if (!receipt?.path) return;
    const { data, error } = await supabase.storage.from("receipt-captures").download(receipt.path);
    if (error || !data) { window.alert("Couldn't download — try View instead."); return; }
    const base = receipt.path.split("/").pop() || "invoice";
    const supplier = (bill.supplier_name || "bill").replace(/[^\w]+/g, "_");
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = base.includes(".") ? `${supplier}_${base}` : `${supplier}.${data.type.includes("pdf") ? "pdf" : "jpg"}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  // T-377 — upload / replace the invoice file. Old file is left in storage
  // (audit) and the swap is stamped into notes.
  async function onFilePicked(f: File | null) {
    if (!f) return;
    setBusy("upload");
    try {
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const safe = f.name.replace(/[^A-Za-z0-9._-]/g, "_");
      const path = `bills/${bill.ap_invoice_id}/${stamp}-${safe}`;
      const { error: upErr } = await supabase.storage.from("receipt-captures")
        .upload(path, f, { contentType: f.type || "application/octet-stream", upsert: false });
      if (upErr) throw upErr;

      const auditNote = pdfPath
        ? `[${new Date().toISOString().slice(0, 10)}] Invoice file replaced (was ${pdfName ?? pdfPath}).`
        : `[${new Date().toISOString().slice(0, 10)}] Invoice file attached.`;
      const { error: updErr } = await supabase.from("ap_invoices").update({
        pdf_storage_path: path,
        pdf_original_filename: f.name,
        notes: [bill.notes, auditNote].filter(Boolean).join("\n"),
      }).eq("ap_invoice_id", bill.ap_invoice_id);
      if (updErr) throw updErr;

      setPdfPath(path); setPdfName(f.name);
      qc.invalidateQueries({ queryKey: ["bills_to_pay"] });
    } catch (e: any) {
      window.alert(`Upload failed: ${e.message ?? e}`);
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  // T-377 — amount adjust with reason (GST assumed 10% on the new total).
  async function saveAmount() {
    const inc = Number(newInc);
    if (!inc || Number.isNaN(inc) || inc <= 0) { window.alert("Enter a valid new total (inc GST)."); return; }
    setBusy("save");
    const { ex } = splitFromInc(inc);   // T-403 — shared lib/gst split
    const auditNote = `[${new Date().toISOString().slice(0, 10)}] Amount adjusted ${fmtMoney(Number(bill.amount_inc_gst ?? 0))} → ${fmtMoney(inc)} (${adjustReason.trim() || "no reason given"}).`;
    const { error } = await supabase.from("ap_invoices").update({
      amount_inc_gst: inc,
      amount_ex_gst: ex,
      notes: [bill.notes, auditNote].filter(Boolean).join("\n"),
    }).eq("ap_invoice_id", bill.ap_invoice_id);
    setBusy(null);
    if (error) { window.alert(`Couldn't update amount: ${error.message}`); return; }
    setAmountInc(inc); setEditAmount(false);
    qc.invalidateQueries({ queryKey: ["bills_to_pay"] });
  }

  async function markPaid() {
    // The list can only contain bills that are in Xero, so this should never
    // fire. It exists because the alternative to a redundant check here is
    // money leaving the account against a bill Xero has never heard of - which
    // is exactly what happened on 20 Aug when the list filter let 'approved'
    // through. A guard on the action survives a filter regressing.
    if (bill.status !== "entered_in_xero") {
      window.alert(
        "This bill isn't in Xero yet, so it can't be marked paid.\n\n"
        + "Send it across on the Xero hand-off screen first (AP > Xero hand-off). "
        + "Once it's in Xero it'll come back here ready to pay.");
      return;
    }
    setBusy("pay");
    const { error } = await supabase.from("ap_invoices").update({
      status:            "paid",
      paid_date:         paidDate || new Date().toISOString().slice(0, 10),
      payment_reference: ref.trim() || null,
    }).eq("ap_invoice_id", bill.ap_invoice_id);
    setBusy(null);
    if (error) { window.alert(`Couldn't mark paid: ${error.message}`); return; }
    onChanged();
  }

  const t = dueTone(bill.due_date);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        {/* (T-387 CopyChip defined at bottom of file) */}
        <div className="p-5 border-b border-line">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold">{bill.supplier_name ?? "Bill"}</h3>
              <div className="text-[12px] text-text-muted mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                {bill.invoice_number && <span className="font-mono">{bill.invoice_number}</span>}
                {bill.xero_bill_number && <span>Xero {bill.xero_bill_number}</span>}
                <span className={t.cls}>{t.label}</span>
                {bill.is_reimbursement && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent/15 text-accent border border-accent/30 font-medium">
                    Reimburse {bill._reimbName ?? "employee"} — pays a person, not a vendor
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[18px] text-text">{fmtMoney(amountInc)}</div>
              <div className="text-[11px] text-text-muted">inc GST</div>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* T-387 — internet-banking copy strip: reference + raw amount, one
              tap each. The reference is what the supplier sees on their side. */}
          <div className="bg-ink-raised border border-accent/25 rounded-md px-3 py-2.5 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Internet banking</span>
            <CopyChip label="Reference" value={bill.invoice_number ?? ""} display={bill.invoice_number ?? "(no invoice #)"} />
            <CopyChip label="Amount" value={Number(amountInc).toFixed(2)} display={Number(amountInc).toFixed(2)} />
            {bill.invoice_number && (
              <CopyChip label="Both" value={`${bill.invoice_number}\t${Number(amountInc).toFixed(2)}`} display="ref + amount" />
            )}
          </div>

          {/* Facts grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-[13px]">
            <div><div className="text-[10px] uppercase tracking-wider text-text-muted">Invoice date</div>{bill.invoice_date ? fmtDate(bill.invoice_date) : "—"}</div>
            <div><div className="text-[10px] uppercase tracking-wider text-text-muted">Due date</div>{bill.due_date ? fmtDate(bill.due_date) : "—"}</div>
            <div><div className="text-[10px] uppercase tracking-wider text-text-muted">Payment method</div>{bill.payment_method ?? "—"}</div>
            <div><div className="text-[10px] uppercase tracking-wider text-text-muted">Budget code</div><span className="font-mono">{bill.budget_code ?? "—"}</span></div>
            <div><div className="text-[10px] uppercase tracking-wider text-text-muted">Project</div><span className="font-mono text-accent">{bill._proj ?? "—"}</span></div>
            <div><div className="text-[10px] uppercase tracking-wider text-text-muted">Ex GST</div>{fmtMoney(Number(bill.amount_ex_gst ?? amountInc / 1.1))}</div>
            <div className="col-span-2 md:col-span-3">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Loaded by</div>
              {capturedByName ?? "—"}
              {bill.captured_at ? ` · ${fmtDate(bill.captured_at)}` : ""}
              {bill.capture_source ? ` (${bill.capture_source})` : ""}
            </div>
          </div>

          {bill.description && (
            <div className="text-[12px] text-text-muted bg-ink-raised border border-line rounded-md px-3 py-2 whitespace-pre-wrap">{bill.description}</div>
          )}

          {/* Amount adjust (T-377 — surcharge) */}
          <div className="border border-line rounded-md p-3">
            {!editAmount ? (
              <button type="button" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent"
                onClick={() => { setNewInc(String(amountInc || "")); setEditAmount(true); }}>
                <Pencil size={13} /> Adjust amount (e.g. card surcharge on payment)
              </button>
            ) : (
              <div className="flex flex-wrap items-end gap-3">
                <Field label="New total (inc GST)">
                  <Input type="number" step="0.01" value={newInc} onChange={e => setNewInc(e.target.value)} className="max-w-[140px]" />
                </Field>
                <Field label="Reason" hint="">
                  <Input value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="card surcharge" className="max-w-[200px]" />
                </Field>
                <div className="flex gap-2 mb-0.5">
                  <Button size="sm" disabled={busy === "save"} onClick={saveAmount}>{busy === "save" ? "Saving…" : "Save"}</Button>
                  <Button size="sm" variant="secondary" onClick={() => setEditAmount(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>

          {/* Invoice file (T-375 view + T-377 attach/replace) */}
          <div className="border border-line rounded-md p-3">
            <div className="flex items-center gap-2.5 flex-wrap">
              <FileText size={16} className={receipt?.url ? "text-accent" : "text-text-muted"} />
              <div className="min-w-0 flex-1 text-[12px]">
                {receipt?.url
                  ? <span className="text-text">{pdfName ?? receipt.path?.split("/").pop() ?? "Invoice file"}</span>
                  : <span className="text-warn">No invoice attached — attach one now or when you pay.</span>}
              </div>
              {receipt?.url && (
                <>
                  <a href={receipt.url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line text-[12px] text-text hover:border-accent transition-colors">
                    <Eye size={13} /> View
                  </a>
                  <button type="button" onClick={downloadReceipt}
                     className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line text-[12px] text-text hover:border-accent transition-colors">
                    <Download size={13} /> Download
                  </button>
                </>
              )}
              <button type="button" disabled={busy === "upload"} onClick={() => fileInput.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-[12px] font-semibold hover:bg-accent-hover disabled:opacity-60 transition-colors">
                <Upload size={13} /> {busy === "upload" ? "Uploading…" : pdfPath ? "Replace invoice" : "Attach invoice"}
              </button>
              <input ref={fileInput} type="file" accept="application/pdf,image/*" className="hidden"
                onChange={e => onFilePicked(e.target.files?.[0] ?? null)} />
            </div>
            {pdfPath && (
              <div className="text-[10px] text-text-muted mt-1.5">Replacing keeps the old file in storage and stamps a note on the bill for audit.</div>
            )}
          </div>

          {/* Mark paid — hidden for one line of a bundled bill. Paying a single
              line of a bill that was sent to Xero as one document would leave
              the rest showing as owing money that has already gone out. */}
          {partOfBundle ? (
            <div className="border border-line rounded-md p-3 text-[12px] text-text-muted">
              This is one line of a bundled reimbursement bill. Pay it from the bill itself,
              on the Bills to pay list — all of its lines settle together.
            </div>
          ) : (
          <div className="border border-line rounded-md p-3">
            <div className="text-[12px] font-medium mb-2 flex items-center gap-1.5"><CheckCircle2 size={14} className="text-accent" /> Pay this bill</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Paid date">
                <Input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} />
              </Field>
              <Field label="Payment ref (NAB)" hint="Optional — or leave blank if reconciled in Xero.">
                <Input value={ref} onChange={e => setRef(e.target.value)} placeholder="e.g. EFT / NAB ref" />
              </Field>
            </div>
          </div>
          )}
        </div>

        <div className="p-4 border-t border-line bg-ink-raised flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {!partOfBundle && (
            <Button icon={CheckCircle2} disabled={busy === "pay"} onClick={markPaid}>{busy === "pay" ? "Saving…" : "Mark paid"}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CopyChip — T-387. Tap to copy a raw value for internet banking (no $ signs,
// no commas). Shows a tick for 1.5s after copying.
// ---------------------------------------------------------------------------
function CopyChip({ label, value, display }: { label: string; value: string; display: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail on http/permissions — fall back to a prompt the
      // user can copy from manually.
      window.prompt(`Copy ${label}:`, value);
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      disabled={!value}
      className={`inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded border font-mono transition-colors ${
        copied
          ? "border-ok/50 text-ok bg-ok/10"
          : value
            ? "border-line text-text hover:border-accent"
            : "border-line text-text-dim cursor-default"
      }`}
      title={value ? `Copy ${label.toLowerCase()} to clipboard` : "Nothing to copy"}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      <span className="text-text-muted font-sans">{label}:</span> {display}
    </button>
  );
}
